import {
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  type ElementNode,
  type LexicalNode,
  type NodeKey,
} from "lexical";
import type { BlockId } from "../../core/types";
import { $getBlockId } from "../block-id/state";
import { $selectedBlocks } from "../block-selection/extension";
import type { DropUnit } from "./geometry";

const $isNestedListWrapper = (node: LexicalNode | null): boolean =>
  node !== null &&
  $isElementNode(node) &&
  node.getType() === "listitem" &&
  node.getFirstChild()?.getType() === "list";

/** The child of `parent` that holds `node`, counting a nested list as part of the item above it. */
function $childOf(parent: ElementNode, node: LexicalNode): LexicalNode | null {
  let current: LexicalNode | null = node;
  while (current !== null && !parent.is(current.getParent())) current = current.getParent();
  if (current === null || !$isNestedListWrapper(current)) return current;
  return current.getPreviousSibling();
}

/**
 * The siblings a text selection runs across, when it starts and ends under `parent` and covers
 * more than one of them. A selection that crosses into another container says nothing here.
 */
function $blocksInRange(parent: ElementNode): LexicalNode[] | null {
  const selection = $getSelection();
  if (!$isRangeSelection(selection) || selection.isCollapsed()) return null;
  const from = $childOf(parent, selection.anchor.getNode());
  const to = $childOf(parent, selection.focus.getNode());
  if (from === null || to === null || from.is(to)) return null;
  const [start, end] = [from.getIndexWithinParent(), to.getIndexWithinParent()].toSorted(
    (a, b) => a - b,
  );
  return parent
    .getChildren()
    .slice(start, end! + 1)
    .filter((child) => !$isNestedListWrapper(child));
}

export interface DragUnit extends DropUnit {
  /** The nodes whose boxes make up this unit: the block, then any nested lists hanging off it. */
  keys: readonly NodeKey[];
}

export interface DragPlan {
  /** Where the dragged blocks may go: only among these siblings. */
  parentKey: NodeKey;
  inList: boolean;
  units: readonly DragUnit[];
  sourceIds: readonly BlockId[];
  /** A few words of what is being dragged, for the preview. */
  label: string;
  count: number;
}

/**
 * Decides, once, when a drag starts, what is being dragged and what it may be dropped between.
 * Dragging a member of a block selection drags the selection, and dragging one of the blocks a
 * text selection runs across drags all of them; anything else drags just that block or list item.
 * Targets are the siblings under the same parent and nothing else, so a drop can never move a
 * block into another container. Reads the document; measures nothing.
 */
export function $planDrag(handleKey: NodeKey): DragPlan | null {
  const node = $getNodeByKey(handleKey);
  const parent = node === null ? null : node.getParent();
  if (node === null || parent === null) return null;
  const inList = parent.getType() === "list";
  if (!inList && !$getRoot().is(parent)) return null;

  const selected = (inList ? null : $selectedBlocks()) ?? $blocksInRange(parent);
  const group = selected !== null && selected.some((block) => block.is(node)) ? selected : [node];
  const sourceKeys = new Set(group.map((block) => block.getKey()));
  const sourceIds = group.map((block) => $getBlockId(block)).filter((id) => id !== null);
  if (sourceIds.length !== group.length) return null;

  const units: DragUnit[] = [];
  for (const child of parent.getChildren()) {
    const last = units[units.length - 1];
    if ($isNestedListWrapper(child) && last !== undefined) {
      units[units.length - 1] = { ...last, keys: [...last.keys, child.getKey()] };
      continue;
    }
    const id = $getBlockId(child);
    if (id === null) return null;
    units.push({ id, moving: sourceKeys.has(child.getKey()), keys: [child.getKey()] });
  }
  const text = group[0]!.getTextContent().trim().replaceAll(/\s+/g, " ");
  return {
    parentKey: parent.getKey(),
    inList,
    units,
    sourceIds,
    label: text.length > 40 ? `${text.slice(0, 40)}…` : text,
    count: group.length,
  };
}
